import type { LedgerEvent } from "./types.js";
import { makeRowKey } from "./row-key.js";
import { delegatedOwnerDigest, maskDelegatedKey, maskPii, maskSecrets, maskUserRef } from "./pii.js";

/** Contact/identity masking (emails, phones, userRef digests) is ON unless the
 *  operator of a self-hosted install opts out — their own team's rows, their
 *  call. Secret masking has no switch (see maskSecrets). Read per call so the
 *  policy is testable and env changes need no rebuild. */
function contactMaskingEnabled(): boolean {
  return process.env.M8T_LEDGER_CONTACT_MASKING !== "off";
}

export const SNIPPET_MAX_LEN = 256;

/** How much collapsed text the PII scan covers. Masking must run BEFORE the
 *  length cap (a secret straddling the cut would otherwise survive as a
 *  recognizable head), and the window bounds regex work on adversarial
 *  megapastes. 16× the cap leaves room for placeholder replacement to shift
 *  text; a token cut by the window edge is dropped whole (sanitizeSnippet) so
 *  no half-scanned text can reach the stored snippet. */
export const PII_SCAN_WINDOW = SNIPPET_MAX_LEN * 16;

/** Single-line, whitespace-collapsed, capped + ellipsized. Privacy-aware. */
export function truncateSnippet(text: string | undefined): string | undefined {
  if (text == null) return undefined;
  const oneLine = text.replace(/\s+/g, " ").trim();
  if (oneLine.length === 0) return undefined;
  return oneLine.length <= SNIPPET_MAX_LEN
    ? oneLine
    : oneLine.slice(0, SNIPPET_MAX_LEN) + "…";
}

/** truncateSnippet + PII masking — the form every stored snippet takes. */
export function sanitizeSnippet(text: string | undefined): string | undefined {
  if (text == null) return undefined;
  const oneLine = text.replace(/\s+/g, " ").trim();
  let windowed = oneLine.slice(0, PII_SCAN_WINDOW);
  // A token cut by the window edge must not keep its unmasked head.
  if (oneLine.length > PII_SCAN_WINDOW) windowed = windowed.replace(/\S+$/, "");
  const masked = contactMaskingEnabled() ? maskPii(windowed) : maskSecrets(windowed);
  return truncateSnippet(masked);
}

/**
 * Map a LedgerEvent → an AgentLedger Table entity.
 * - PartitionKey = agentName; RowKey = descending-time (makeRowKey).
 * - The logical `timestamp` is stored physically as `eventTimestamp` to
 *   avoid Table Storage's reserved system `Timestamp` column. The brain engine
 *   reads `eventTimestamp`. (Physical detail, not a contract change.)
 * - undefined/null optional fields are omitted (Table Storage rejects them).
 * - artifacts is stored as a JSON string (Table cells are scalar); read side parses it.
 */
export function toLedgerEntity(e: LedgerEvent): Record<string, unknown> {
  const entity: Record<string, unknown> = {
    partitionKey: e.agentName,
    rowKey: makeRowKey(e.timestamp, e.eventId),
    eventId: e.eventId,
    eventTimestamp: e.timestamp,
    agentName: e.agentName,
    source: e.source,
    outcome: e.outcome,
  };
  const optional: Record<string, unknown> = {
    channel: e.channel,
    // Persisted pseudonymized: email-shaped refs and a delegated end-user key
    // become stable digests here so no producer can write a raw UPN/email or a
    // caller-asserted key (pii.ts has the full policy; self-hosted operators
    // can opt out — see contactMaskingEnabled).
    // The delegated end-user key is digested EITHER WAY: it is a live session
    // credential, and the contact opt-out covers an operator's own team's
    // contact info, not someone else's bearer token.
    userRef: contactMaskingEnabled() ? maskUserRef(e.userRef) : maskDelegatedKey(e.userRef),
    // The delegated owner's conversation-registry digest — written for `svc:`
    // refs only, and written whatever the masking policy says, because it is
    // what lets retention bind this row to the conversation the visitor owns
    // once the ref beside it is a pseudonym. See delegatedOwnerDigest.
    ownerDigest: delegatedOwnerDigest(e.userRef),
    foundryConversationId: e.foundryConversationId,
    responseId: e.responseId,
    model: e.model,
    inputSnippet: sanitizeSnippet(e.inputSnippet),
    replySnippet: sanitizeSnippet(e.replySnippet),
    latencyMs: e.latencyMs,
    errorCode: e.errorCode,
    agentKind: e.agentKind,
    initiatingAgent: e.initiatingAgent,
    delegationId: e.delegationId,
    parentEventId: e.parentEventId,
    depth: e.depth,
    pendingItem: sanitizeSnippet(e.pendingItem),
    // Table Storage cells are scalar → serialize the pointer array to a JSON string
    // (the read side JSON.parses it). Same physical-mapping rationale as eventTimestamp.
    // Paths and MIME strings are model-chosen free text, so they go through the
    // masker too — ordinary artifact paths are untouched (path charset never
    // matches a rule), but a filename embedding an address or key cannot land raw.
    artifacts: e.artifacts?.length
      ? JSON.stringify(
          e.artifacts.map((a) => ({
            path: maskPii(a.path),
            mime: a.mime === undefined ? undefined : maskPii(a.mime),
          })),
        )
      : undefined,
  };
  for (const [k, v] of Object.entries(optional)) {
    if (v !== undefined && v !== null) entity[k] = v;
  }
  return entity;
}
